/**
 * Auth Service — Okta SPA authentication wrapper for the Word Add-in.
 *
 * When `__MSS_AUTH_DISABLED__` is `true`, every function is a no-op
 * so the add-in works without Okta configuration.
 *
 * When enabled, uses `@okta/okta-auth-js` with Authorization Code +
 * PKCE via the Office Dialog API (see ADR-009).
 */

import { OktaAuth } from "@okta/okta-auth-js";

// ── Singleton ──

let oktaAuth: OktaAuth | null = null;

// ── Auth-failure notification ──

type AuthFailureListener = () => void;
const authFailureListeners = new Set<AuthFailureListener>();

export function onAuthFailure(listener: AuthFailureListener): () => void {
  authFailureListeners.add(listener);
  return () => { authFailureListeners.delete(listener); };
}

/**
 * Clears stored tokens and sends the user back to the sign-in screen.
 * Call this whenever the API rejects a request with 401.
 */
export function notifyAuthFailure(): void {
  oktaAuth?.tokenManager.clear();
  authFailureListeners.forEach((fn) => fn());
}

/**
 * Initialise the OktaAuth client.
 *
 * Safe to call multiple times — returns the existing instance if
 * already initialised.  In dev builds this is a no-op.
 */
export function initAuth(): void {
  if (__MSS_AUTH_DISABLED__) {
    return;
  }
  if (oktaAuth) {
    return;
  }

  try {
    oktaAuth = new OktaAuth({
      issuer: __MSS_OKTA_ISSUER__,
      clientId: __MSS_OKTA_CLIENT_ID__,
      pkce: true,
      scopes: ["openid", "profile", "email", "offline_access"],
      redirectUri: `${window.location.origin}/auth-callback.html`,
      tokenManager: { autoRenew: true },
    });

    oktaAuth.start();
  } catch (err: unknown) {
    console.error("[auth] initAuth:", err);
    throw err;
  }
}

// ── Public API ──

/**
 * Returns a non-expired access token string, or `null` when
 * unavailable.  In dev builds always returns `null`.
 *
 * `tokenManager.get` reads storage without validating `exp`, so an
 * expired token must be renewed explicitly — otherwise it is sent to
 * the API and rejected with a 401.  When renewal fails (e.g. the Okta
 * session also lapsed) this signals auth failure so the UI returns to
 * the sign-in screen rather than surfacing a generic fetch error.
 */
export async function getAccessToken(): Promise<string | null> {
  if (__MSS_AUTH_DISABLED__) {
    return null;
  }
  if (!oktaAuth) {
    return null;
  }

  const accessToken = oktaAuth.tokenManager.getTokensSync().accessToken;
  if (!accessToken) {
    return null;
  }
  if (!oktaAuth.tokenManager.hasExpired(accessToken)) {
    return accessToken.accessToken;
  }

  try {
    // Concurrent calls are de-duplicated by the token manager.
    const renewed = await oktaAuth.tokenManager.renew("accessToken");
    if (renewed && "accessToken" in renewed) {
      return renewed.accessToken;
    }
  } catch (err: unknown) {
    console.warn("[auth] access token renewal failed:", err);
  }

  notifyAuthFailure();
  return null;
}

/**
 * Opens the Okta login page in an Office Dialog API popup.
 *
 * The popup loads `auth-callback.html` which initiates the Okta
 * redirect.  After login, Okta redirects back to the same page
 * which parses the tokens and sends them to the task pane via
 * `messageParent`.
 *
 * Resolves with the access token string once the user completes
 * login.  Rejects if the dialog is closed or an error occurs.
 */
export function loginWithDialog(): Promise<string> {
  if (__MSS_AUTH_DISABLED__) {
    return Promise.resolve("");
  }

  const callbackUrl = `${window.location.origin}/auth-callback.html`;

  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      callbackUrl,
      { height: 60, width: 40, promptBeforeOpen: false },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          const err = new Error(`Dialog failed: ${asyncResult.error.message}`);
          console.error("[auth] loginWithDialog:", err);
          reject(err);
          return;
        }

        const dialog = asyncResult.value;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg) => {
            dialog.close();
            try {
              const message = JSON.parse((arg as { message: string }).message);
              if (message.error) {
                const err = new Error(message.error);
                console.error("[auth] loginWithDialog (callback error):", err);
                reject(err);
              } else {
                console.log("[auth] loginWithDialog (callback success):", message);
                oktaAuth!.tokenManager.setTokens(message.tokens);
                resolve(message.tokens.accessToken.accessToken);
              }
            } catch (err: unknown) {
              console.error("[auth] loginWithDialog (parse/setTokens):", err);
              reject(err instanceof Error ? err : new Error(String(err)));
            }
          },
        );

        dialog.addEventHandler(
          Office.EventType.DialogEventReceived,
          (arg) => {
            // 12006 = dialog closed by user
            const err = new Error(`Dialog closed (code: ${(arg as { error: number }).error})`);
            console.error("[auth] loginWithDialog (dialog event):", err);
            reject(err);
          },
        );
      },
    );
  });
}

/**
 * The signed-in user's email, from the ID token's claims. `null` in dev
 * builds and before sign-in. Falls back to `sub`, which Okta populates with
 * the email when the `email` claim is not in the ID token.
 */
export function getUserEmail(): string | null {
  if (__MSS_AUTH_DISABLED__) {
    return null;
  }

  const idToken = oktaAuth?.tokenManager.getTokensSync().idToken;
  return idToken?.claims.email ?? idToken?.claims.sub ?? null;
}

/**
 * The signed-in user's opaque Okta subject identifier, for telemetry.
 *
 * Deliberately `sub` with no email fallback, unlike `getUserEmail`: this value
 * is sent to Application Insights and stored by Azure, so it must not be
 * personally identifying. Reads the already-stored ID token, so there is no
 * network call and no additional scope required.
 */
export function getUserId(): string | null {
  if (__MSS_AUTH_DISABLED__) {
    return null;
  }

  const sub = oktaAuth?.tokenManager.getTokensSync().idToken?.claims?.sub;
  return typeof sub === "string" ? sub : null;
}

/**
 * Whether the user currently has a valid (non-expired) access token.
 *
 * In dev builds this always returns `true` so auth gating is skipped.
 */
export async function isAuthenticated(): Promise<boolean> {
  if (__MSS_AUTH_DISABLED__) {
    return true;
  }
  if (!oktaAuth) {
    return false;
  }

  return (await getAccessToken()) !== null;
}

/**
 * Revokes access token, clears local state, and ends the Okta SSO session.
 */
export async function logout(): Promise<void> {
  if (__MSS_AUTH_DISABLED__) {
    return;
  }
  if (!oktaAuth) {
    return;
  }

  const idToken = oktaAuth.tokenManager.getTokensSync().idToken;

  try {
    await oktaAuth.revokeAccessToken();
  } catch (err: unknown) {
    console.warn("[auth] revokeAccessToken failed:", err);
  }

  oktaAuth.tokenManager.clear();

  const redirectUri = `${window.location.origin}/logout-callback.html`;
  const idTokenHint = idToken ? (idToken as { idToken: string }).idToken : "";
  const signoutUrl =
    `${__MSS_OKTA_SIGNOUT_URL__}?id_token_hint=${idTokenHint}&post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;

  Office.context.ui.displayDialogAsync(
    signoutUrl,
    { height: 40, width: 30, promptBeforeOpen: false },
    (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        const dialog = asyncResult.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, () => {
          dialog.close();
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          dialog.close();
        });
      }
    },
  );
}
